// @ts-nocheck
import { formGlobals } from "./_/form-globals";
import BaseInput from './_/base-input';

/**
 * Class: CheckboxGroupInput
 * Purpose: Manage a checkbox group (role="group"), validate "min 1" when required,
 *          set ARIA (aria-required on group only), chain aria-describedby (note/error) on each item,
 *          and emit events consistent with other inputs.
 *
 * Constructor Parameters (types):
 *   wrap (HTMLElement)      - The .form_field-wrap around the group/note/error.
 *   formIndex (number|null) - Form index.
 *   wrapIndex (number|null) - Field index inside the form.
 *
 * Public API:
 *   isValid(): boolean
 *   getState(): { values:string[], selectedCount:number, valid:boolean, blurred:boolean, required:boolean, id:string }
 *   validateNow(): void
 *   destroy(): void
 *
 * Events:
 *   'validchange' (detail:{ valid, values, selectedCount, instance })
 *   'valuechange' (detail:{ values, selectedCount, instance })
 *   'focus'       (detail:{ instance })
 *   'blur'        (detail:{ instance })
 * 
 * @class
 * @extends {BaseInput}
 */
export default class CheckboxGroupInput extends BaseInput {
    /**
     * @param {HTMLElement} wrap
     * @param {number|null} formIndex
     * @param {number|null} wrapIndex
     */
    constructor(wrap, formIndex = null, wrapIndex = null) {
      super(wrap, formIndex, wrapIndex);
  
      // Struktur
      this.group = wrap.querySelector('[role="group"]'); // .form_field-group
      this.checkboxes = Array.from(wrap.querySelectorAll('input[type="checkbox"]'));
      this.list = wrap.querySelector('.form_field-group_checkbox_list') || this.group;
  
      // Optional: Hidden Aggregator Input innerhalb der Gruppe (falls vorhanden)
      this.hiddenInput = this.group?.querySelector('input[type="hidden"]') || null;
  
      // IDs
      this.inputId = `checkboxgroup-${formIndex}-${wrapIndex}`;
      this.errorId = `${this.inputId}-error`;
  
      // Initialize base class elements
      this._initErrorElement();
      this._initNoteElement(this.group?.querySelector(formGlobals.noteSelector) || null);
  
      // Required: entweder am Group-Container oder (zur Robustheit) an irgendeinem Item angegeben
      const groupReq = this.group?.getAttribute('aria-required') === 'true';
      const itemReq = this.checkboxes.some((cb) => cb.getAttribute('aria-required') === 'true');
      this.required = groupReq || itemReq;
  
      // State
      this.blurred = false;
      this.values = this._selectedValues();
      this.selectedCount = this.values.length;
      this.valid = this.required ? this.selectedCount >= 1 : true;
      this._syncHiddenInput();
  
      // Listener mgmt
      this._onFocusIn = this._onFocusIn.bind(this);
      this._onFocusOut = this._onFocusOut.bind(this);
      this._onChange = this._onChange.bind(this);
  
      this._setAttributes();
      this._normalizeRequiredARIA(); // <-- NEU: nur Gruppe trägt aria-required
      this._setCheckboxNames(); // Setze name-Attribute basierend auf Hidden-Input
      this._initEvents();
      this._setFilledClass();
      this._syncHiddenInput();
    }

    /** @returns {HTMLInputElement[]} */
    _getAriaElement() {
      return this.checkboxes;
    }

    _getValidChangeDetail(): { valid: boolean; values: string[]; selectedCount: number; instance: CheckboxGroupInput } {
      return {
        valid: this.valid,
        values: [...this.values],
        selectedCount: this.selectedCount,
        instance: this
      };
    }
  
    // ---------- Public ----------
    isValid() {
      return this.valid;
    }
  
    getState() {
      return {
        values: [...this.values],
        selectedCount: this.selectedCount,
        valid: this.valid,
        blurred: this.blurred,
        required: this.required,
        id: this.inputId,
      };
    }
  
    validateNow() {
      this._validate();
    }
  
    destroy() {
      super.destroy();
      this.group = null;
      this.checkboxes = [];
      this.hiddenInput = null;
    }
  
    // ---------- Private ----------
    _setAttributes() {
      if (!this.group) return;
  
      // initial: jedes Item bekommt describedby = Note (falls vorhanden)
      this._setAriaDescribedBy({ includeNote: true, includeError: false });
    }
  
    _normalizeRequiredARIA() {
      if (!this.group) return;
  
      // Nur die Gruppe trägt aria-required
      if (this.required) this.group.setAttribute('aria-required', 'true');
      else this.group.removeAttribute('aria-required');
  
      // An allen Items aria-required entfernen (Best Practice)
      this.checkboxes.forEach((cb) => cb.removeAttribute('aria-required'));
    }

    _setCheckboxNames() {
      if (!this.hiddenInput) return;
      
      const baseName = this.hiddenInput.getAttribute('name');
      if (!baseName) return;
      
      // Setze name-Attribute für jede Checkbox: baseName-0, baseName-1, etc.
      this.checkboxes.forEach((cb, index) => {
        cb.setAttribute('name', `${baseName}-${index}`);
      });
    }
  
    _initEvents() {
      const o = { signal: this._ac.signal };
  
      // Fokus mgmt auf Gruppenebene
      this.group.addEventListener('focusin', this._onFocusIn, o);
      this.group.addEventListener('focusout', this._onFocusOut, o);
  
      // Änderungen pro Checkbox
      this.checkboxes.forEach((cb) => cb.addEventListener('change', this._onChange, o));
    }
  
    _onFocusIn() {
      this.wrap.classList.add(formGlobals.focusedClass);
      if (!this._focused) {
        this._focused = true;
        this._dispatch('focus', { instance: this });
      }
    }
  
    _onFocusOut(e) {
      if (!this.group.contains(e.relatedTarget)) {
        this.wrap.classList.remove(formGlobals.focusedClass);
        const wasFirst = !this.blurred;
        this.blurred = true;
        this._focused = false;
        this._dispatch('blur', { instance: this });
        if (wasFirst && this.required) this._validate();
      }
    }
  
    _onChange() {
      const prevKey = this.values.join('|');
  
      this.values = this._selectedValues();
      this.selectedCount = this.values.length;
  
      this._setFilledClass();
      this._syncHiddenInput();
  
      if (this.values.join('|') !== prevKey) {
        this._dispatch('valuechange', {
          values: [...this.values],
          selectedCount: this.selectedCount,
          instance: this,
        });
      }
  
      if (this.blurred || this.required) this._validate();
    }
  
    _selectedValues() {
      return this.checkboxes.filter((cb) => cb.checked).map((cb) => cb.value ?? '');
    }
  
    _setFilledClass() {
      this.wrap.classList.toggle(formGlobals.filledClass, this.selectedCount > 0);
    }
  
    _syncHiddenInput() {
      if (!this.hiddenInput) return;
      this.hiddenInput.value = this.values.join(', ');
    }
  
    _validate() {
      const ok = this.required ? this.selectedCount >= 1 : true;
      if (ok) this._setValid();
      else this._setInvalid();
    }
  
  }
  